import type { QuizItemKind } from '../../../quiz/list-item';
import { PractiseCard, type PractiseCardProps } from '../../components/PractiseCard';
import { cn } from '../../utils/cn';
import type { QuizListRowProps } from './QuizListRow.types';

/**
 * Our three stored activity kinds → the DS PractiseCard's fixed type glyphs
 * and tints. This map is the reason the block exists: it was written out four
 * separate times (web's `quiz-item-card`, mobile's `QuizCard` /
 * `OralExamCard` / `CaseStudyCard`) and each copy was free to drift on which
 * glyph an oral exam gets.
 */
const KIND_TYPE: Record<QuizItemKind, PractiseCardProps['type']> = {
  quiz: 'quiz',
  caseStudy: 'caseStudy',
  oral: 'audio',
};

/**
 * One row of the deck's Exam & Quizzes list.
 *
 * This block deliberately **wraps** the design-system {@link PractiseCard}
 * rather than redrawing it: Figma `108:27330`'s quiz row and the DS "Quiz
 * cards updated" component (`3153:44675`) that PractiseCard already
 * implements are the same component — badge + `⋯` on the first line, title on
 * the second, status pill on the third. What was genuinely duplicated across
 * the four call sites is the mapping *into* that card — kind → glyph and
 * score → pill tone — so that, and only that, lives here.
 *
 * Everything platform-bound (navigation, the actions menu, credit-gated
 * retakes) stays with the caller: `onPress` and the `renderMenu` slot.
 */
export function QuizListRow({
  item,
  view = 'grid',
  isExamSimulation = false,
  stretch = false,
  statusLabel,
  typeLabel,
  dateText,
  onPress,
  renderMenu,
  className,
  testID,
}: QuizListRowProps) {
  const type = item.kind === 'quiz' && isExamSimulation ? 'exam' : KIND_TYPE[item.kind];

  return (
    <PractiseCard
      view={view}
      type={type}
      typeLabel={typeLabel}
      title={item.title}
      dateText={dateText}
      // A null score IS the "resumable, not finished" signal (see
      // `QuizListItemBase.percent`) — deriving the tone from it here keeps the
      // orange in-progress pill from depending on each caller remembering.
      status={{
        tone: item.percent === null ? 'in-progress' : 'completed',
        label: statusLabel,
      }}
      onPress={onPress}
      actionsSlot={renderMenu?.()}
      // Ahead of `className` so a caller can still override, but no caller has
      // to remember `w-full` to avoid a 181px tile in a full-width list.
      className={cn(stretch && 'h-auto w-full max-w-none', className)}
      testID={testID}
    />
  );
}
(QuizListRow as { displayName?: string }).displayName = 'QuizListRow';
